import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    categoryMap = {};
    locationMap = {};
    lastCategory = '';
    lastLocation = '';
    lastSearch = '';
    interval = null;

    connect() {
        this.initializeFilters();
    }

    disconnect() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    async initializeFilters() {
        window.addEventListener('DOMContentLoaded', async () => {
            await this.loadCategoryAndLocationMappings();            
            this.applyFiltersFromUrl();
            this.startMonitoringFilterChanges();
        });
    }

    slugify(str) {
        return str
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/ß/g, "ss")
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .toLowerCase();
    }

    normalizeCategoryName(name) {
        const match = name.match(/^[A-Z_]+/i);
        if (!match) return '';
        return match[0].replace(/[^a-zA-Z]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
    }

    applyFiltersFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const selectedFilters = {
            categories: params.get("categories")?.split(',') || [],
            locations: params.get("places")?.split(',') || [],
            search: params.get("search") || ''
        };

        selectedFilters.categories.forEach((cat, i) => {
            const selected = this.categoryMap[cat];
            if (
              selected &&
              window.dvinciJobWidgetConfig.filters &&
              window.dvinciJobWidgetConfig.filters.categories &&
              window.dvinciJobWidgetConfig.filters.categories.selected
            ) {
                window.dvinciJobWidgetConfig.filters.categories.selected[i] = selected;
            }
        });

        selectedFilters.locations.forEach((loc, i) => {
            const selected = this.locationMap[loc] || this.locationMap[loc.toUpperCase()];
            if (
              selected &&
              window.dvinciJobWidgetConfig.filters &&
              window.dvinciJobWidgetConfig.filters.locations &&
              window.dvinciJobWidgetConfig.filters.locations.selected
            ) {
                window.dvinciJobWidgetConfig.filters.locations.selected[i] = selected;
            }
        });

        if (selectedFilters.search) {
            window.dvinciJobWidgetConfig.filters.keywords.selected.push(selectedFilters.search);
        }
    }

    getSelectedValues(forId) {
        const label = document.querySelector(`label[for="${forId}"]`);
        if (!label) return [];
        const filterSection = label.closest('.dvinci-job-filter');
        
        return Array.from(filterSection?.querySelectorAll('.dvinci-select__multi-value__label') || [])
          .map(el => this.slugify(el.textContent.trim()));
    }

    getSearchInput() {
        return document.getElementById('keywords')?.value || '';
    }

    startMonitoringFilterChanges() {
        const observerConfig = { childList: true, subtree: true };

        this.observer = new MutationObserver(() => {
            const currentCategories = this.getSelectedValues('categories');
            const currentLocations = this.getSelectedValues('locations');
            const currentSearch = this.getSearchInput();

            if (JSON.stringify(currentCategories) !== JSON.stringify(this.lastCategory)) {
                this.lastCategory = currentCategories;
                this.updateUrlParam('categories', currentCategories);
            }

            if (JSON.stringify(currentLocations) !== JSON.stringify(this.lastLocation)) {
                this.lastLocation = currentLocations;
                this.updateUrlParam('places', currentLocations);
            }

            if (currentSearch !== this.lastSearch) {
                this.lastSearch = currentSearch;
                this.updateUrlParam('search', currentSearch);
            }
        });

        const filterSection = this.element || document.querySelector('.dvinci-job-widget');
        if (filterSection) {
            this.observer.observe(filterSection, observerConfig);
        }
    }

    updateUrlParam(param, value) {
        const url = new URL(window.location);
        if (Array.isArray(value) && value.length) {
            url.searchParams.set(param, value.join(','));
        } else if (typeof value === 'string' && value.trim()) {
            url.searchParams.set(param, value);
        } else {
            url.searchParams.delete(param);
        }

        history.replaceState(null, '', url);
    }

    toPascalCase(input, mode) {
        let target = input;

        if (mode === 'category') {
            const matches = input.match(/^[A-Z_]+/i);
            if (!matches) return '';
            target = matches[0];
        }

        return target
            .split(/[^a-zA-Z]+/)
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('');
    }

    async loadCategoryAndLocationMappings() {
        try {
            const response = await fetch('https://jobs.wieland.com/jobPublication/list.json');

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();

            data.forEach(({ jobOpening }) => {
                jobOpening.categories.forEach((cat) => {
                    const cleanKey = this.slugify(cat.name);
                    this.categoryMap[this.normalizeCategoryName(cat.internalName)] = cat.internalName;
                    this.categoryMap[cleanKey] = cat.internalName;
                    this.categoryMap[this.toPascalCase(cat.internalName, 'category')] = cat.internalName;
                });

                const localeMap = { 'ö': 'o', 'Ö': 'O', 'ä': 'a', 'Ä': 'A', 'ü': 'u', 'Ü': 'U', 'ß': 'ss' };

                jobOpening.locations.forEach((loc) => {
                    const name = loc.name ?? '';
                    const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                    const mapped = normalized.replace(/[äÄöÖüÜß]/g, char => localeMap[char]);                    
                    this.locationMap[mapped.toUpperCase()] = name.toUpperCase();
                    this.locationMap[this.toPascalCase(mapped, 'location')] = name.toUpperCase();                  
                });
            });
        } catch (error) {
            console.error('Error loading category/location data:', error);
        }
    }
}
